import { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { AlertTriangle } from "lucide-react"
import { mostrarSpinner, ocultarSpinner } from "../../components/Spinner"
import ClientService from "../../services/client/ClientService"
import { showError, showSuccess, showWarning, notifyApiError } from "../../components/alertas"
import { useLocation } from 'react-router-dom';
import { usePushNotifications } from "../../hooks/usePushNotifications"
import ScheduleTab from "./components/horarios"
import AppointmentsTab from "./components/citas"
import ConnectionsTab from "./components/conexiones"
import ServicesTab from "./components/servicios"
import BotResponsesTab from "./components/BotResponsesTab"
import AccountTab from "./components/cuenta"
import IncomesTab from "./components/ingresos"
import PersonalizationTab from "./components/personalizacion"
import GuideTab from "./components/guia"
import SocialTab from "./components/redes"
import LoyaltyTab from "./components/lealtad"
import logger from "../../utils/logger";
import Tour from "../../components/tour/Tour";
import InstallPrompt from "../../components/InstallPrompt";
import { useTour, isTourDone } from "../../components/tour/useTour";
import { TAB_TOURS, WELCOME_TOUR } from "./tourSteps";
import PanelShell from "./components/PanelShell";
import { NAV_ITEMS, GUIDE_PENDING, GUIDE_DONE, STAFF_HIDDEN, NEEDS_SUBSCRIPTION } from "./navigation";

function AdminDashboard({ preloadedImages, requiredTab, onboardingMessage, onConfigComplete, completedSteps, totalSteps, brand, onViewAgenda, openGuide }) {
  const location = useLocation();
  const userData = JSON.parse(localStorage.getItem('userData')) || {};
  const isAdmin = userData?.userType === 'admin';
  const isStaff = userData?.userType === 'staff';
  const [showOnboardingModal, setShowOnboardingModal] = useState(!!requiredTab);
  const [clientData, setClientData] = useState(null);
  // page.jsx desmonta y vuelve a montar el panel cada vez que recarga los datos del
  // negocio (loading), así que la pestaña inicial ya decide: con configuración
  // pendiente, o recién completada en esta sesión (openGuide), se abre la guía.
  const [activeTab, setActiveTab] = useState(() => (!isStaff && (requiredTab || openGuide) ? "guide" : "appointments"));
  const [services, setServices] = useState(clientData?.business?.services || [])
  const [blockBase, setBlockBase] = useState(null);
  const [schedule, setSchedule] = useState({});
  const [isLoadingClientData, setIsLoadingClientData] = useState(false);

  // ---- Recorrido guiado ----
  // Bienvenida una sola vez; cada pestaña explica sus secciones la primera vez que
  // se abre; el botón de ayuda repite el de la pestaña actual. Persistido por cliente.
  const tourClientId = localStorage.getItem('clientId');
  const tours = useMemo(() => ({
    welcome: WELCOME_TOUR.filter((st) => st.id !== 'steps' || !clientData?.configComplete),
    ...TAB_TOURS,
  }), [clientData?.configComplete]);
  const tour = useTour({ tours, clientId: tourClientId, activeTab, setActiveTab });

  useEffect(() => {
    if (!clientData || tour.run) return;
    const welcomeDone = isTourDone(tourClientId, 'welcome');
    const id = !welcomeDone ? 'welcome' : (!isTourDone(tourClientId, activeTab) && TAB_TOURS[activeTab] ? activeTab : null);
    if (!id) return;
    // Dejar que la pestaña termine de montar (y que el modal de onboarding no compita).
    const t = setTimeout(() => tour.start(id), id === 'welcome' ? 900 : 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientData, activeTab, tour.run]);
  const isWhatsAppConnected = clientData?.whatsintegration?.status === 'authenticated' || clientData?.whatsintegration?.status === 'ready';
  const isGoogleCalendarConnected = clientData?.staff?.some(s => s.status === 'ready');
  const pushPermission = usePushNotifications({ isAdmin, isStaff });

  const fetchClientData = useCallback(async () => {
    if (clientData) return;
    const clientId = localStorage.getItem('clientId');
    if (!clientId || isLoadingClientData) return;
    setIsLoadingClientData(true);
    try {
      mostrarSpinner();
      const data = await ClientService.getClientDataById(clientId);
      setClientData(data);
    } catch (error) {
      logger.error("Error loading client data:", error);
      showError("Error", "No se pudo cargar la información del cliente.");
    } finally {
      ocultarSpinner();
      setIsLoadingClientData(false);
    }
  }, [clientData, isLoadingClientData]);

  // Recargar todo el panel conservando la pestaña. Instalada como PWA no hay botón de
  // recargar del navegador, y cada pestaña carga sus propios datos (citas, ingresos),
  // así que una recarga completa es lo único que garantiza ver información fresca
  // (y de paso toma la última versión publicada).
  const reloadPanel = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.set('tab', activeTab);
    window.location.replace(url.toString());
    return new Promise(() => {}); // el indicador sigue girando hasta que cargue la página nueva
  }, [activeTab]);

  // Función para refrescar clientData (sin la condición de clientData existente)
  const refetchClientData = useCallback(async () => {
    const clientId = localStorage.getItem('clientId');
    if (!clientId) return;
    try {
      const data = await ClientService.getClientDataById(clientId);
      setClientData(data);
    } catch (error) {
      logger.error("Error refreshing client data:", error);
    }
  }, []);

  useEffect(() => {
    // Solo mostrar advertencia después de 2 segundos (cuando ya todo cargó)
    const timer = setTimeout(() => {
      if (isAdmin && pushPermission === 'default') {
        showWarning("Activa las notificaciones para recibir alertas en tiempo real.");
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [isAdmin, pushPermission]);

  useEffect(() => {
    fetchClientData();
  }, [fetchClientData]);

  // Con configuración pendiente el panel abre en "Primeros pasos" (el modal restringe,
  // esa sección explica cada paso). Al completar el último paso en la sesión se enseña
  // una vez "Así funciona" (misma sección, ya en modo completo). Un profesional (staff)
  // no ve la guía: va directo a la pestaña del paso, como antes.
  const hadPendingSetup = useRef(!!requiredTab);
  useEffect(() => {
    setShowOnboardingModal(!!requiredTab);
    if (requiredTab) {
      hadPendingSetup.current = true;
      if (isStaff) {
        setActiveTab(requiredTab === 'google' || requiredTab === 'whatsapp' ? 'connections' : requiredTab);
      } else {
        setActiveTab('guide');
      }
    } else if (hadPendingSetup.current) {
      hadPendingSetup.current = false;
      if (!isStaff) setActiveTab('guide');
    }
  }, [requiredTab, isStaff]);

  useEffect(() => {
    // Solo cerrar el modal y avanzar si ambos están conectados
    if (
      (requiredTab === 'google' || requiredTab === 'whatsapp') &&
      isGoogleCalendarConnected &&
      isWhatsAppConnected
    ) {
      setShowOnboardingModal(false);
      onConfigComplete();
    }
  }, [isGoogleCalendarConnected, isWhatsAppConnected, requiredTab, onConfigComplete]);


  // Estado para los colores personalizados
  const [customColors, setCustomColors] = useState({
    primaryColor: '#0d6efd',
    primaryDark: '#0056b3'
  })

  // Estado para la personalización del logo/icono
  const [brandSettings, setBrandSettings] = useState({})

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tab = queryParams.get('tab');
    const googleCalendarStatus = queryParams.get('googleCalendar');

    if (tab) {
      setActiveTab(tab);
    }

    if (googleCalendarStatus === 'connected') {
      showSuccess("Google Calendar conectado", "Tu cuenta de Google Calendar se ha conectado exitosamente.");
    } else if (googleCalendarStatus === 'error') {
      const errorMessage = queryParams.get('message') || "Ocurrió un error al conectar Google Calendar.";
      showError("Error al conectar Google Calendar", errorMessage);
    }
  }, [location.search]);


  useEffect(() => {
    setBrandSettings({
      logoType: clientData?.personalization?.logoType || 'icon',
      selectedIcon: clientData?.personalization?.selectedIcon || 'Scissors',
      logoImage: clientData?.personalization?.logoImage || null,
      businessName: clientData?.business?.name,
      slogan: clientData?.business?.slogan || '',
      phone: Array.isArray(clientData?.business?.phones) ? clientData.business.phones.join(', ') : (clientData?.business?.phones || ''),
      email: clientData?.business?.email || '',
      address: clientData?.business?.address || '',
      googleMapsLink: clientData?.business?.mapsUrl || '',
      links: {
        whatsapp: clientData?.business?.links?.whatsapp || '',
        instagram: clientData?.business?.links?.instagram || '',
        facebook: clientData?.business?.links?.facebook || '',
        twitter: clientData?.business?.links?.twitter || '',
        tiktok: clientData?.business?.links?.tiktok || '',
      },
      confirmWebAppointment: clientData?.personalization?.confirmWebAppointment ?? false,
      format24Hours: clientData?.personalization?.format24Hours ?? false,
      staffLabel: clientData?.personalization?.staffLabel || '',
      announcement: {
        enabled: clientData?.personalization?.announcement?.enabled ?? false,
        title: clientData?.personalization?.announcement?.title || '',
        message: clientData?.personalization?.announcement?.message || '',
      },
    });
    setBlockBase(clientData?.business?.blockBase || null);
    setServices(clientData?.business?.services || [])
    setCustomColors({
      primaryColor: clientData?.personalization?.primaryColor || '#0d6efd',
      primaryDark: clientData?.personalization?.primaryDark || '#0056b3'
    });
    setSchedule(clientData?.schedule || {});
  }, [clientData, requiredTab]);


  // Secciones visibles según rol; con suscripción inactiva algunas se deshabilitan.
  const subscriptionActive = clientData?.subscription?.isActive;
  // La guía va primera mientras falte configuración y al final cuando ya está completa.
  const navItems = useMemo(() => (requiredTab ? [GUIDE_PENDING, ...NAV_ITEMS] : [...NAV_ITEMS, GUIDE_DONE])
    .filter((it) => !(isStaff && STAFF_HIDDEN.includes(it.id)))
    .filter((it) => !it.addon || !!clientData?.subscription?.addons?.[it.addon])
    .map((it) => ({ ...it, disabled: !!clientData && !subscriptionActive && NEEDS_SUBSCRIPTION.includes(it.id) })),
  [isStaff, clientData, subscriptionActive, requiredTab]);


  // Función para actualizar colores dinámicamente
  const updateColors = (newColors) => {
    const root = document.documentElement

    // Actualizar variables CSS del global.css
    root.style.setProperty('--primary-color', newColors.primaryColor)
    root.style.setProperty('--primary-dark', newColors.primaryDark)

    // Convertir color hex a rgba para las sombras
    const hexToRgba = (hex, alpha) => {
      const r = parseInt(hex.slice(1, 3), 16)
      const g = parseInt(hex.slice(3, 5), 16)
      const b = parseInt(hex.slice(5, 7), 16)
      return `rgba(${r}, ${g}, ${b}, ${alpha})`
    }

    // Actualizar variables de sombra dinámicas
    root.style.setProperty('--primary-shadow', hexToRgba(newColors.primaryColor, 0.3))
    root.style.setProperty('--primary-shadow-hover', hexToRgba(newColors.primaryColor, 0.4))

    // Actualizar la clase .text-primary del index.css
    let textPrimaryStyle = document.getElementById('dynamic-text-primary')
    if (!textPrimaryStyle) {
      textPrimaryStyle = document.createElement('style')
      textPrimaryStyle.id = 'dynamic-text-primary'
      document.head.appendChild(textPrimaryStyle)
    }

    textPrimaryStyle.textContent = `
      .text-primary {
        color: ${newColors.primaryColor} !important;
      }
    `

    setCustomColors(newColors)
  }

  const handleSaveSchedule = (updatedSchedule) => {
    setSchedule(updatedSchedule);
    handleUpdateClient({
      business: { ...clientData.business },
      schedule: updatedSchedule
    }, "Horarios").then(() => onConfigComplete());
  };

  const handleUpdateClient = async (editingData, modulo, filesData) => {
    try {
      mostrarSpinner();
      const updateData = buildUpdateData(editingData);
      const filteredUpdateData = Object.keys(updateData).reduce((acc, key) => {
        if (Object.keys(updateData[key]).length > 0) {
          acc[key] = updateData[key];
        }
        return acc;
      }, {});
      await ClientService.updateClientSelf(clientData.id, filteredUpdateData, filesData || {});
      showSuccess(`${modulo} actualizada`, "✓ Cambios guardados exitosamente.");
    } catch (error) {
      logger.error("Error actualizando:", error);
      notifyApiError(error, `Guardar ${modulo}`);
    } finally {
      ocultarSpinner(false);
    }
  }

  const buildUpdateData = (data) => {
    const updateData = {};

    // Personalización (solo si hay valores)
    if (data.personalization && Object.keys(data.personalization).some(key => data.personalization[key] !== undefined)) {
      updateData.personalization = data.personalization;
    }

    // Business (solo campos con valores)
    if (data.business) {
      // Normalizar servicios si vienen en data.business
      if (data.business.services) {
        data.business.services = data.business.services.map(service => ({
          ...service,
          price: Number(service.price) || 0,
          durationMinutes: Number(service.durationMinutes) || 0,
          requiresAdvance: service.requiresAdvance === true || service.requiresAdvance === 'true',
          advanceAmount: service.requiresAdvance ? (Number(service.advanceAmount) || null) : null,
          images: service.images || null
        }));
      }
      updateData.business = data.business;
    } else {
      const businessData = {};
      if (data.businessName) businessData.name = data.businessName;
      if (data.slogan) businessData.slogan = data.slogan;
      if (data.phone) businessData.phones = data.phone.split(',').map(s => s.trim()).filter(Boolean);
      if (data.address) businessData.address = data.address;
      if (data.email) businessData.email = data.email;
      if (data.googleMapsLink) businessData.mapsUrl = data.googleMapsLink;
      if (data.links && Object.values(data.links).some(val => val)) businessData.links = data.links;
      if (data.services) {
        businessData.services = data.services.map(service => ({
          ...service,
          price: Number(service.price) || 0,
          durationMinutes: Number(service.durationMinutes) || 0,
          requiresAdvance: service.requiresAdvance === true || service.requiresAdvance === 'true',
          advanceAmount: service.requiresAdvance ? (Number(service.advanceAmount) || null) : null,
          images: service.images || null
        }));
      }
      if (data.blockBase) businessData.blockBase = data.blockBase;
      if (Object.keys(businessData).length > 0) updateData.business = businessData;
    }

    // BotConfig (solo si hay respuestas o default)
    const botConfigData = {};
    if (data.botConfig) {
      if (data.botConfig.botResponses) botConfigData.responses = data.botConfig.botResponses;
      if (data.botConfig.responses) botConfigData.responses = data.botConfig.responses;
      if (data.botConfig.ignoredNumbers) botConfigData.ignoredNumbers = data.botConfig.ignoredNumbers;
      if (data.botConfig.confirmationMessage !== undefined) botConfigData.confirmationMessage = data.botConfig.confirmationMessage;
      if (data.botConfig.welcomeMessage !== undefined) botConfigData.welcomeMessage = data.botConfig.welcomeMessage;
      if (data.botConfig.reminders) botConfigData.reminders = data.botConfig.reminders;
      if (data.botConfig.blacklist) botConfigData.blacklist = data.botConfig.blacklist;
      if(data.botConfig.whatsappBookingEnabled !== undefined) botConfigData.whatsappBookingEnabled = data.botConfig.whatsappBookingEnabled;
    }
    if (Object.keys(botConfigData).length > 0) updateData.botConfig = botConfigData;

    if (data.schedule) {
      updateData.schedule = data.schedule;
    }

    return updateData;
  };

  // Función para resetear a colores por defecto
  const resetToDefault = () => {
    const defaultColors = {
      primaryColor: '#0d6efd',
      primaryDark: '#0056b3'
    }
    updateColors(defaultColors)
    localStorage.removeItem('customColors')
  }

  // Función para guardar configuración de marca
  const handleSaveBrand = async () => {
    const updateData = {
      business: {
        name: brandSettings.businessName,
        slogan: brandSettings.slogan,
        phones: brandSettings.phone.split(',').map(s => s.trim()).filter(Boolean),
        address: brandSettings.address,
        email: brandSettings.email,
        mapsUrl: brandSettings.googleMapsLink || null,
        links: brandSettings.links,
        services: services.map(service => ({
          ...service,
          price: Number(service.price) || 0,
          durationMinutes: Number(service.durationMinutes) || 0,
          requiresAdvance: service.requiresAdvance === true || service.requiresAdvance === 'true',
          advanceAmount: service.requiresAdvance ? (Number(service.advanceAmount) || null) : null,
          images: service.images || null
        })),
        blockBase: blockBase
      },
      personalization: {
        logoType: brandSettings.logoType,
        selectedIcon: brandSettings.selectedIcon,
        logoImage: brandSettings.logoType === 'image' ? brandSettings.logoImage : null,
        primaryColor: customColors.primaryColor,
        primaryDark: customColors.primaryDark,
        confirmWebAppointment: brandSettings.confirmWebAppointment,
        format24Hours: brandSettings.format24Hours,
        staffLabel: (brandSettings.staffLabel || '').trim(), // vacío => la UI usa "Profesional"
        announcement: {
          enabled: !!brandSettings.announcement?.enabled,
          title: (brandSettings.announcement?.title || '').trim(),
          message: (brandSettings.announcement?.message || '').trim(),
          updatedAt: new Date().toISOString(),
        },
        // Indicador para eliminar logo del R2 si se cambió a icono
        deleteLogoFile: brandSettings.logoType === 'icon' && clientData?.personalization?.logoImage ? true : false,
      }
    };

    // Preparar archivos si hay un logo para subir
    const filesArray = [];
    if (brandSettings.logoFile && brandSettings.logoType === 'image') {
      filesArray.push(brandSettings.logoFile);
    }

    await handleUpdateClient(updateData, "Personalización de Marca", filesArray);
  }



  // Función para manejar subida de imagen
  const handleImageUpload = (event) => {
    const file = event.target.files[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        const newSettings = {
          ...brandSettings,
          logoImage: e.target.result,
          logoFile: file, // Guardar el archivo original también
          logoType: 'image'
        }
        setBrandSettings(newSettings)
        // Actualizar en tiempo real
        window.dispatchEvent(new Event('brandSettingsUpdated'))
      }
      reader.readAsDataURL(file)
    }
  }

  // Función para actualizar configuración de marca en tiempo real
  const updateBrandSettings = (newSettings) => {
    setBrandSettings(newSettings)
    window.dispatchEvent(new Event('brandSettingsUpdated'))
  }


  // Cargar colores guardados al montar el componente
  useEffect(() => {
    const savedColors = localStorage.getItem('customColors')
    if (savedColors) {
      const parsedColors = JSON.parse(savedColors)
      updateColors(parsedColors)
    }

    const savedBrand = localStorage.getItem('brandSettings')
    if (savedBrand) {
      setBrandSettings(JSON.parse(savedBrand))
    }
  }, [])

  const checkSubscriptionLimits = (type) => {
    const sub = clientData?.subscription;
    if (!sub?.isActive) return { allowed: false, message: 'Suscripción inactiva. Actualiza tu plan.' };

    switch (type) {
      case 'staff': {
        // Enforce de asientos: cada profesional ocupa un asiento contratado (seats).
        const seatLimit = sub.seats ?? 1;
        return (clientData?.staff?.length || 0) >= seatLimit
          ? { allowed: false, message: `Alcanzaste el límite de tu plan (${seatLimit} profesional(es)). Aumenta tus asientos para agregar más.` }
          : { allowed: true };
      }
      // 'responses' ya no tiene límite: el plan Base incluye respuestas ilimitadas.
      default:
        return { allowed: true };
    }
  };

  const Spinner = () => (
    <div className="w-100 h-100 d-flex align-items-center justify-content-center bg-secondary rounded">
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Cargando...</span>
      </div>
    </div>
  );


  return (
    <PanelShell
      brand={brand}
      items={navItems}
      activeTab={activeTab}
      onSelect={setActiveTab}
      onHelp={() => tour.start(TAB_TOURS[activeTab] ? activeTab : 'welcome')}
      onViewAgenda={onViewAgenda}
      onRefresh={reloadPanel}
    >
      {/* Modal de Onboarding */}
      {showOnboardingModal && !tour.run && (
        <div className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 d-flex align-items-center justify-content-center" style={{ zIndex: 1050 }}>
          <div className="bg-white rounded shadow-lg p-4" style={{ maxWidth: '500px' }}>
            <h5 className="text-primary">Configuración Obligatoria</h5>
            <p>
              {(requiredTab === 'google' || requiredTab === 'whatsapp')
                ? "Debes conectar WhatsApp y al menos un profesional con Google Calendar para continuar."
                : onboardingMessage}
            </p>
            <p>Completa este paso para continuar al siguiente.</p>
            {/* Indicador de progreso */}
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <small className="text-muted">Progreso</small>
                <small className="text-muted">{requiredTab ? `${Math.floor((completedSteps / totalSteps) * 100)}%` : '100%'}</small>
              </div>
              <div className="progress" style={{ height: '8px' }}>
                <div
                  className="progress-bar bg-primary"
                  style={{ width: `${requiredTab ? (completedSteps / totalSteps) * 100 : 100}%` }}
                ></div>
              </div>
            </div>
            <button className="app-btn app-btn-primary"
              onClick={() => {
                setShowOnboardingModal(false);
                if (!isStaff) setActiveTab('guide');
              }}
            >
              Ir a Configurar
            </button>
          </div>
        </div>
      )}

      {/* Banner de suscripción inactiva */}
      {clientData?.subscription && !clientData.subscription.isActive && (
        <div className="cp-banner" role="status">
          <AlertTriangle size={18} />
          <div><strong>Suscripción inactiva.</strong> Actualiza tu plan para acceder a todas las funciones.</div>
        </div>
      )}

      {/* Sugerencia de instalar como app (no aparece si ya está instalada) */}
      <InstallPrompt appName="tu panel" />

      {activeTab === "guide" && !isStaff && (
        <GuideTab
          clientData={clientData}
          setupPending={!!requiredTab}
          refetchClientData={refetchClientData}
          onGo={setActiveTab}
        />
      )}

      {activeTab === "account" && (
        <AccountTab 
          clientData={clientData} 
          userData={userData} 
          handleClientUpdate={handleUpdateClient}
          refetchClientData={refetchClientData}
        />
      )}

      {/* Bot Responses Tab */}
      {activeTab === "bot" && (
        <BotResponsesTab
          handleUpdateClient={handleUpdateClient}
          clientData={clientData}
          checkSubscriptionLimits={checkSubscriptionLimits}
          refetchClientData={refetchClientData}
          canEditAssistant={!isStaff}
        />
      )}

      {activeTab === "social" && (
        <SocialTab brandSettings={brandSettings} updateBrandSettings={updateBrandSettings} handleSaveBrand={handleSaveBrand} />
      )}

      {/* Services Tab */}
      {activeTab === "services" && (
        <ServicesTab
          clientData={clientData}
          preloadedImages={preloadedImages}
          onConfigComplete={onConfigComplete}
          handleUpdateClient={handleUpdateClient}
          brandSettings={brandSettings}
          blockBase={blockBase}
          setBlockBase={setBlockBase}
          services={services}
          setServices={setServices}
        />
      )}

      {/* Appointments Tab */}
      {activeTab === "appointments" && (
        clientData
          ? <AppointmentsTab clientData={clientData} userData={userData} />
          : <Spinner />
      )}

      {/* Colors Customization Tab */}
      {activeTab === "colors" && (
        <PersonalizationTab
          customColors={customColors}
          updateColors={updateColors}
          brandSettings={brandSettings}
          setBrandSettings={setBrandSettings}
          updateBrandSettings={updateBrandSettings}
          resetToDefault={resetToDefault}
          handleSaveBrand={handleSaveBrand}
          handleImageUpload={handleImageUpload}
          services={services}
          clientData={clientData}
        />
      )}

      {activeTab === "connections" && (
        <ConnectionsTab clientData={clientData}
          checkLimits={checkSubscriptionLimits}
          onConfigComplete={onConfigComplete}
          userData={userData}
          refetchClientData={refetchClientData}
        />
      )}

      {activeTab === "schedule" && (
        <ScheduleTab
          schedule={schedule}
          onSaveSchedule={handleSaveSchedule}
          userData={userData}
          clientData={clientData}
          refetchClientData={refetchClientData}
        />
      )}

      {activeTab === "incomes" && (
        <IncomesTab clientData={clientData} />
      )}

      {activeTab === "loyalty" && clientData?.subscription?.addons?.loyaltyCards && (
        <LoyaltyTab clientData={clientData} refetchClientData={refetchClientData} canEditProgram={!isStaff} />
      )}

      {/* Modal para agregar respuesta del bot */}

      {tour.run && (
        <Tour
          step={tour.run.steps[tour.run.index]}
          target={tour.run.target}
          index={tour.shown.index}
          total={tour.shown.total}
          onNext={tour.next}
          onPrev={tour.prev}
          onClose={tour.close}
          onSkip={tour.close}
          onSkipAll={tour.skipAll}
        />
      )}
    </PanelShell>
  )
}

export default AdminDashboard;